// accountBalancesTable.js - Enhances the account balances table with status and comments.

// Store fetched data mapping postingItemId to {status, comment, commentUpdatedAt, commentUpdatedBy}
// Ensure this is defined only once across all concatenated files.
if (typeof window.Lux_postingItemDataMap === 'undefined') {
  window.Lux_postingItemDataMap = {}
}

// Reference to the MutationObserver instance
let Lux_Table_tableObserver = null

function Lux_Table_newRowsAdded(mutationsList) {
  const dataRows = []

  for (mutation of mutationsList) {
    if (mutation.addedNodes.length > 0) {
      dataRows.push(...mutation.addedNodes)
    }
  }
  if (dataRows.length > 0) {
    enhanceNewRows(dataRows)
  }
}

/**
 * Initializes the enhancement process for the account balances table.
 * Sets up a MutationObserver to wait for the table to appear.
 */
function Lux_Table_initializeAccountBalancesTableEnhancements(user) {
  console.log('Initializing Account Balances Table Enhancements...')

  // Disconnect previous observer if exists
  if (Lux_Table_tableObserver) {
    Lux_Table_tableObserver.disconnect()
    console.log('Disconnected previous table observer.')
  }

  const observerOptions = {
    childList: true, // Watch for addition/removal of child nodes
    subtree: true, // Watch descendants as well
  }

  Lux_Table_tableObserver = new MutationObserver((mutationsList, observer) => {
    // Look for the specific table structure we need
    if (Object.keys(window.Lux_postingItemDataMap).length > 0) {
      Lux_Table_updateAllStatusIcons()
      observer.disconnect()
      return
    }

    const overlayWrappers = document.querySelectorAll('div.overlay-wrapper')
    if (overlayWrappers.length < 2) return // Need the second wrapper

    const tableContainer = overlayWrappers[1].querySelector(
      '.ant-table-container'
    )
    if (!tableContainer) return

    const tableBody = tableContainer.querySelector('tbody.ant-table-tbody')
    const dataRows = tableBody
      ? Array.from(
          tableBody.querySelectorAll('tr.ant-table-row:not(.lux-row-enhanced)')
        )
      : []

    let newRows = false
    for (mutation of mutationsList) {
      if (mutation.target === tableBody && mutation.addedNodes.length > 0) {
        prepareTable(tableContainer, user)
        const tableObserver = new MutationObserver(Lux_Table_newRowsAdded)
        tableObserver.observe(tableBody, {
          childList: true,
        })
        observer.disconnect()
        newRows = true
      }
    }

    if (newRows) {
      enhanceNewRows(dataRows)
      Lux_Table_updateAllStatusIcons()
    }
  })

  // Start observing the document body for changes
  Lux_Table_tableObserver.observe(document.body, observerOptions)
  console.log('Table observer started, waiting for table...')

  // Timeout safeguard in case the table never appears or observer fails
  setTimeout(() => {
    if (Lux_Table_tableObserver) {
      console.warn('Table observer timed out after 30 seconds. Disconnecting.')
      Lux_Table_tableObserver.disconnect()
      Lux_Table_tableObserver = null
    }
  }, 30000) // 30 seconds timeout*
}

function prepareTable(tableContainer, user) {
  const headerRows = tableContainer.querySelectorAll('thead.ant-table-thead tr')
  const tableBody = tableContainer.querySelector('tbody.ant-table-tbody')
  const dataRows = tableBody
    ? Array.from(tableBody.querySelectorAll('tr.ant-table-row'))
    : [] // Get current rows
  if (headerRows.length < 2) {
    // Only check header rows for initial setup
    console.warn(
      'Header structure not as expected. Cannot set up status column.'
    )
    return
  }

  // --- Setup Header/Col Structure (Run Once) ---
  const statusHeaderExists = headerRows[1]?.querySelector(
    '.lux-invoice-status-header'
  )
  if (!statusHeaderExists) {
    // Add Colgroup Element
    const colgroups = tableContainer.querySelectorAll('colgroup')
    colgroups.forEach((colgroup) => {
      const lohnanteilCol = colgroup.querySelector('col:nth-last-child(3)')
      if (lohnanteilCol && !colgroup.querySelector('.lux-status-col')) {
        const newCol = document.createElement('col')
        newCol.style.width = '80px' // Consistent width
        newCol.classList.add('lux-status-col')
        colgroup.insertBefore(newCol, lohnanteilCol)
      }
    })

    // Add Header Cell
    const firstHeaderRow = headerRows[0]
    const secondHeaderRow = headerRows[1]
    if (secondHeaderRow) {
      const lohnanteilTh = secondHeaderRow.querySelector('th:nth-last-child(1)')
      if (lohnanteilTh) {
        const newHeaderCell = document.createElement('th')
        newHeaderCell.className = 'ant-table-cell lux-invoice-status-header' // Use prefix
        newHeaderCell.textContent = 'Status'
        secondHeaderRow.insertBefore(newHeaderCell, lohnanteilTh)
        console.log('Added Status header cell.')

        // Adjust colspan in the first header row if necessary
        const buchungHeader = firstHeaderRow?.querySelector('th[colspan]')
        if (buchungHeader) {
          const currentColspan = parseInt(
            buchungHeader.getAttribute('colspan') || '0',
            10
          )
          if (currentColspan > 0) {
            buchungHeader.setAttribute(
              'colspan',
              (currentColspan + 1).toString()
            )
            console.log(
              `Adjusted colspan in first header row to ${currentColspan + 1}`
            )
          }
        }
      } else {
        console.warn(
          "Could not find 'Lohnanteil' header cell to insert status header before."
        )
      }
    }

    // Add empty data cells initially
    dataRows.forEach((row) => {
      const wageShareTd = row.querySelector('td:nth-last-child(3)')
      const cellExists = row.querySelector('.lux-invoice-status-cell')
      if (!cellExists) {
        const statusCell = document.createElement('td')
        statusCell.className = 'ant-table-cell lux-invoice-status-cell' // Use prefix
        statusCell.style.textAlign = 'center'
        statusCell.style.cursor = 'pointer' // Make it look clickable

        if (wageShareTd) {
          row.insertBefore(statusCell, wageShareTd)
        } else {
          // Fallback if 'Lohnanteil' cell isn't found (e.g., different table structure)
          console.warn(
            "Could not find 'Lohnanteil' data cell, appending status cell."
          )
          row.appendChild(statusCell)
        }
      }
    })

    // --- Add Delegated Event Listener for Clicks (Idempotent - Add Once) ---
    if (tableBody && !tableBody.dataset.luxStatusClickListenerAdded) {
      tableBody.addEventListener('click', async (event) => {
        // Make async for fetch
        const targetCell = event.target.closest('.lux-invoice-status-cell')
        if (targetCell && targetCell.dataset.postingItemId) {
          const postingItemId = parseInt(targetCell.dataset.postingItemId, 10)
          if (isNaN(postingItemId)) return

          console.log(`Status icon clicked for postingItemId: ${postingItemId}`)

          // Ensure Controller and Modal functions exist
          if (
            typeof AccountBalanceStatusController !== 'function' ||
            typeof Lux_Modal_showStatusModal !== 'function'
          ) {
            console.error(
              'AccountBalanceStatusController or Lux_Modal_showStatusModal not found!'
            )
            alert('Error: Cannot open status editor.')
            return
          }

          // Create Controller instance
          const controller = new AccountBalanceStatusController(postingItemId)

          // --- Define Listeners for Table/Global Actions ---
          // Store references for cleanup
          const listeners = {}

          listeners.onUpdate = (data) => {
            console.log('Table Script: Controller emitted update', data)
            Lux_Table_updateAllStatusIcons()
          }
          listeners.onSaveSuccess = (data) => {
            console.log('Table Script: Controller emitted saveSuccess', data)
            Lux_Modal_hideStatusModal() // Close modal
            // Cleanup listeners after handling
            Object.keys(listeners).forEach((event) =>
              controller.off(event, listeners[event])
            )
          }
          listeners.onApproveSuccess = (data) => {
            console.log('Table Script: Controller emitted approveSuccess', data)
            Lux_Modal_hideStatusModal() // Close modal
            // Cleanup listeners after handling
            Object.keys(listeners).forEach((event) =>
              controller.off(event, listeners[event])
            )
          }
          // Note: Errors during save/approve are handled *within* the modal by its own listeners

          // --- Subscribe Listeners relevant to the Table Script ---
          controller.on('update', listeners.onUpdate)
          controller.on('saveSuccess', listeners.onSaveSuccess)
          controller.on('approveSuccess', listeners.onApproveSuccess)
          console.log(
            `Table Script: Subscribed listeners for controller ID ${controller.id}`
          )

          // --- Show the modal, passing the controller ---
          // The modal will subscribe its *own* listeners for internal UI updates
          Lux_Modal_showStatusModal(controller, 'single order', user)

          // --- Fetch initial data ---
          // Fetching after showing modal allows modal to display loading state
          await controller.fetchInitialData()
        }
      })
      tableBody.dataset.luxStatusClickListenerAdded = 'true'
      console.log('Added delegated click listener for status cells.')
    }
  } // End of one-time header/col setup

  return !statusHeaderExists
}

function enhanceNewRows(dataRows) {
  const idsToFetch = []
  const currentDataMap = window.Lux_postingItemDataMap || {}
  dataRows.forEach((row) => {
    if (row.classList.contains('lux-row-enhanced')) {
      return // Skip if already processed
    }

    // Ensure status cell exists for this row
    let statusCell = row.querySelector('.lux-invoice-status-cell')
    if (!statusCell) {
      const wageShareTd = row.querySelector('td:nth-last-child(3)')
      statusCell = document.createElement('td')
      statusCell.className = 'ant-table-cell lux-invoice-status-cell' // Use prefix
      statusCell.style.textAlign = 'center'
      if (wageShareTd) {
        row.insertBefore(statusCell, wageShareTd)
      } else {
        console.warn(
          "Could not find 'Lohnanteil' data cell, appending status cell."
        )
        row.appendChild(statusCell)
      }
    }

    // Mark row as processed structurally
    row.classList.add('lux-row-enhanced')

    // Check if data needs fetching for this row
    const key = row.getAttribute('data-row-key')
    const postingItemId = key ? parseInt(key, 10) : null

    if (
      postingItemId !== null &&
      !(postingItemId in currentDataMap) &&
      !idsToFetch.includes(postingItemId)
    ) {
      // Check if ID is valid, not already cached, and not already queued for fetch
      idsToFetch.push(postingItemId)
    }
  })

  // --- Fetch Data for New Rows (if any) ---
  if (idsToFetch.length > 0) {
    console.log(
      `Fetching latest comments/statuses for ${idsToFetch.length} new/uncached items.`
    )

    Lux_ApiClient_get(
      `/api/account-balances?postingItemIds=${idsToFetch.join(',')}`
    )
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || `API error: ${response.status}`)
        }
        return response.json()
      })
      .then((newDataMap) => {
        console.log('Received new comment/status data:', newDataMap)
        // Merge new data into the global map
        // Ensure existing entries are properly updated/overwritten
        // Convert keys to numbers if they aren't already, just in case
        for (const key in newDataMap) {
          if (Object.hasOwnProperty.call(newDataMap, key)) {
            const postingItemId = parseInt(key, 10)
            if (!isNaN(postingItemId)) {
              window.Lux_postingItemDataMap[postingItemId] = newDataMap[key]
            }
          }
        }
        // Update icons for all rows now that cache is updated
        Lux_Table_updateAllStatusIcons()
      })
      .catch((error) => {
        console.error(
          'Error fetching or processing new comments/statuses:',
          error
        ) // Update log
        // Optionally mark only the newly fetched rows with error, or rely on updateAll
        idsToFetch.forEach((failedId) => {
          const row = tableContainer.querySelector(
            `tr[data-row-key="${failedId}"]`
          )
          const statusCell = row?.querySelector('.lux-invoice-status-cell')
          if (statusCell) {
            statusCell.textContent = '⚠️'
            statusCell.title = `Error loading status: ${error.message}`
          }
        })
        // Still update others that might have loaded correctly before or were cached
        Lux_Table_updateAllStatusIcons()
      })
  } else {
    Lux_Table_updateAllStatusIcons()
  }
}

/**
 * Updates all status icons in the table based on the globally stored Lux_postingItemDataMap.
 * Ensures rows marked as enhanced have their icons updated.
 */
function Lux_Table_updateAllStatusIcons() {
  // Select only rows that have been structurally enhanced to avoid errors on partially loaded tables
  const enhancedRows = document.querySelectorAll(
    'tbody.ant-table-tbody tr.ant-table-row.lux-row-enhanced'
  )
  const dataMap = window.Lux_postingItemDataMap || {} // Use cached data
  const emojiMap = window.Lux_statusEmojiMap || {}

  enhancedRows.forEach((row) => {
    const statusCell = row.querySelector('.lux-invoice-status-cell')
    if (!statusCell) return // Skip if cell not found

    const postingItemIdStr = row.getAttribute('data-row-key')
    const postingItemId = postingItemIdStr
      ? parseInt(postingItemIdStr, 10)
      : null

    if (postingItemId === null || isNaN(postingItemId)) {
      statusCell.textContent = '?' // Indicate missing ID
      statusCell.title = 'Missing postingItemId'
      delete statusCell.dataset.postingItemId
      return
    }

    // Get data from cache
    const itemData = dataMap[postingItemId]
    const statusText = itemData?.status ?? 'open' // Default to 'open'
    const commentText = itemData?.comment ?? ''

    const emoji = emojiMap[statusText] || emojiMap['open'] // Fallback emoji
    statusCell.textContent = emoji

    statusCell.title = ''
    if (commentText) {
      statusCell.title += `Kommentar: ${commentText}`
    }

    // Ensure postingItemId is set for the click handler
    statusCell.dataset.postingItemId = postingItemId.toString()
    statusCell.style.cursor = 'pointer' // Ensure clickable appearance
  })
}